import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useFloatingPanel } from '../hooks/useFloatingPanel';
import { LAYER } from './layers';
import { Tooltip } from './Tooltip';

export interface FolderTab<K extends string> {
  /** Also the React list key — must be unique. */
  key: K;
  /** Counts go inside here via `<CountBadge>`; there is no separate count prop. */
  label: ReactNode;
  disabled?: boolean;
  tone?: 'red';
  /**
   * Hides the tab, and with it the panel below, from a caller without the key. A tab is
   * the entrance to its content, so gating the two separately is how a user ends up
   * clicking a tab onto an error state or an empty panel.
   *
   * The content still has to be gated where it fetches — the tab strip only decides what
   * is offered. `disabled` is for state ("no rows yet"), never for permission: a control
   * the caller can never enable is noise.
   */
  permission?: string;
}

/**
 * slate-300 — the panel's own edge, and with it the ACTIVE tab's outline.
 *
 * A step darker than the shell's ordinary dividers, on a product call: the active tab and the
 * panel are one surface, so they are drawn with one line. The surface the strip merges into
 * must use this too, or the line breaks at the seam.
 */
const PANEL_BORDER = '#cbd5e1';

/**
 * slate-200 — an INACTIVE tab's own outline, a step back from the panel's.
 *
 * An inactive tab is behind the panel, not part of it, and its outline says so. Darkening it
 * to match brought every tab forward and cost the strip the one cue that separates the tab
 * you are on from the ones you are not.
 */
const TAB_BORDER = '#e2e8f0';

/** The panel's ground. The active tab closes its bottom edge in this, which merges the two. */
const PANEL = '#ffffff';

/**
 * A TAB IS 36px TALL, ALWAYS — product call, 05 Oct 2026.
 *
 * It used to be `py-2` over a one-line label, which is the same 36px *as long as the label
 * stays on one line*. Nothing made it stay: the tabs were ordinary flex items, so a strip with
 * more of them than the row could hold SHRANK them until their text wrapped, and the three
 * that wrapped stood a line taller than the three that did not. A row of controls at three
 * different heights is not a row.
 *
 * The height is declared now rather than implied, and `shrink-0` is what keeps a tab at the
 * width its label needs. The strip overflowing is what the dropdown below is for.
 */
const TAB_HEIGHT = 36;

/**
 * The widest a tab may be before its label ellipsizes.
 *
 * A folder nav over a facet draws one tab per VALUE, and a value is data — "Information and
 * Communications Technology Equipment" is a real category name. Uncapped, one of those is a
 * tab wider than the strip, and it pushes every other tab into the dropdown on its own.
 *
 * 16rem clears every fixed label in the product, so the cap only ever fires on a value the
 * strip was never meant to carry.
 */
const MAX_TAB_WIDTH = 256;

/** `gap-1` between tabs, in px — the fit arithmetic has to agree with the class. */
const STRIP_GAP = 4;

/**
 * The tab's ground. Active is the panel's own white, so the two read as one surface; an
 * inactive tab sits a shade back on the strip and lifts toward white on hover.
 */
function surfaceOf<K extends string>(tab: FolderTab<K>, isActive: boolean): string {
  if (isActive) {
    return 'bg-white font-medium';
  }

  return tab.disabled ? 'bg-slate-50' : 'bg-slate-50 hover:bg-white';
}

/**
 * The selected tab's label is the darkest text on the strip — CLAUDE.md, "the selected thing
 * is black, never a tinted colour". The selection is carried by the tint behind it, so an
 * unselected tab differs by weight and contrast rather than by hue.
 */
function labelOf<K extends string>(tab: FolderTab<K>, isActive: boolean): string {
  if (tab.disabled) {
    return 'cursor-not-allowed text-slate-300';
  }

  if (!isActive) {
    return 'text-slate-600';
  }

  return tab.tone === 'red' ? 'text-red-700' : 'text-slate-900';
}

/**
 * Everything a tab's `<button>` wears, shared by the strip and by the hidden measuring row.
 *
 * The two MUST agree, or the row measures a width the strip does not render — so there is one
 * string rather than two that happen to match today.
 *
 * `-mb-px` + `z-10` keep the active tab's overlap ON TOP of the panel below. Without the
 * z-index the panel — later in the DOM, and carrying a `sticky z-20` table header right at its
 * top edge — paints over that 1px and cuts away the merge that makes the tab and the panel one
 * surface. That showed as a distorted active tab in table view and nowhere else.
 */
const TAB_SHAPE =
  'relative z-10 -mb-px inline-flex shrink-0 items-center overflow-hidden rounded-t-lg border px-3 text-xs whitespace-nowrap transition';

/**
 * The label, clipped with an ellipsis rather than wrapped.
 *
 * `[&>*]:ml-4` replaces the `gap-4` the button used to carry: the label is one truncating
 * block now rather than two flex items, so the only element inside it — the `<CountBadge>`,
 * at every call site in the product — takes its separation from a margin instead. The spacing
 * is unchanged; what changed is which box owns it.
 *
 * KNOWN COST, stated rather than discovered: `label` is an opaque `ReactNode`, so the clip
 * takes the whole of it and a badge sitting past the cap is clipped with the tail of the text.
 * The fix, if that ever bites, is to split the contract into a plain-text name and a count and
 * let this component draw the badge itself — not to widen the cap until it stops firing.
 */
const TAB_LABEL = 'min-w-0 truncate [&>*]:ml-4 [&>*]:align-middle';

function sameWidths(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => Math.abs(value - b[index]) < 0.5);
}

/**
 * Which tabs stay in the strip, and which go to the dropdown.
 *
 * Fills from the left and stops at the first tab that does not fit, so the strip reads in the
 * declared order. **The ACTIVE tab is always kept**, wherever it sits — a selection the reader
 * cannot see is a panel chosen by a control that is not on screen, which is the rule the
 * filter rail's own visibility override already runs on.
 */
function fitTabs(
  widths: number[],
  moreWidth: number,
  available: number | null,
  activeIndex: number,
): number[] {
  const count = widths.length;
  const all = widths.map((_, index) => index);

  // Before the first measurement there is nothing to fit against — draw them all for a frame.
  if (available === null || count === 0) {
    return all;
  }

  const total = widths.reduce((sum, width) => sum + width, 0) + STRIP_GAP * (count - 1);
  if (total <= available) {
    return all;
  }

  const budget = available - moreWidth - STRIP_GAP;
  const kept = new Set<number>();
  let used = 0;

  if (activeIndex >= 0) {
    kept.add(activeIndex);
    used = widths[activeIndex];
  }

  for (let index = 0; index < count; index += 1) {
    if (kept.has(index)) continue;

    const next = used + (used > 0 ? STRIP_GAP : 0) + widths[index];
    if (next > budget) break;

    used = next;
    kept.add(index);
  }

  return all.filter((index) => kept.has(index));
}

/**
 * The **standard horizontal folder nav** — CLAUDE.md names the pattern; this renders it.
 *
 * Folder-style primary tabs (reference §6): every tab outlined, the active one merged into
 * the panel below. Underlined tabs are never used anywhere in this product, and no surface
 * hand-rolls a tab strip.
 *
 * Permission-gated tabs are hidden, and the strip owns the consequence: if the active tab
 * is one the caller cannot see, it selects the first one they can, so no page opens onto a
 * panel that is not there.
 *
 * A strip wider than the room it has keeps as many tabs as fit and puts the rest in a
 * dropdown at the end — see `fitTabs`.
 */
export function FolderTabs<K extends string>({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  rightSlot,
  paddingX = 'px-6',
  id,
}: {
  tabs: FolderTab<K>[];
  activeKey: K;
  onChange: (key: K) => void;
  ariaLabel?: string;
  /** Search or counts pinned to the right of the tab strip. */
  rightSlot?: ReactNode;
  paddingX?: string;
  id?: string;
}) {
  const { hasPermission } = useAuth();

  const visible = tabs.filter((tab) => !tab.permission || hasPermission(tab.permission));

  /*
    The default tab is written by the page, which cannot know what this caller holds. Left
    alone, a hidden active tab renders a strip with nothing selected over a panel the caller
    is not allowed to see — so the strip corrects the selection instead.
  */
  const activeVisible = visible.some((tab) => tab.key === activeKey);
  const firstVisibleKey = visible[0]?.key;

  useEffect(() => {
    if (!activeVisible && firstVisibleKey !== undefined) {
      onChange(firstVisibleKey);
    }
  }, [activeVisible, firstVisibleKey, onChange]);

  const stripRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  /** The room the tabs AND the dropdown trigger have to share. */
  const [available, setAvailable] = useState<number | null>(null);
  /** Each tab at its natural width, capped — what the strip would actually render. */
  const [natural, setNatural] = useState<number[]>([]);
  const [moreWidth, setMoreWidth] = useState(0);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, panelRef, rect } = useFloatingPanel(open, close);

  /*
    Measured off a hidden row rather than off the strip, and that is the whole reason the row
    exists: a tab the strip has already pushed into the dropdown is not rendered, so there is
    nothing left to measure it from. The row carries every tab at full width, always.

    OBSERVED rather than re-read on every render. A label carries a count, so a width changes
    without the tab set changing — and the row is a flex row, so its own width is the sum of
    its children's. Watching the row therefore catches a tab added, a tab removed and a count
    going from 9 to 10 alike, with no dependency on anything this closure would go stale on.

    The last child is the dropdown trigger, never a tab. Reading it by position rather than by
    `visible.length` is what keeps the effect free of a stale closure.
  */
  useLayoutEffect(() => {
    const row = measureRef.current;
    if (!row) return;

    const read = () => {
      const widths = Array.from(row.children).map(
        (child) => (child as HTMLElement).getBoundingClientRect().width,
      );

      const more = widths.pop() ?? 0;
      const tabWidths = widths.map((width) => Math.min(width, MAX_TAB_WIDTH));

      setNatural((previous) => (sameWidths(previous, tabWidths) ? previous : tabWidths));
      setMoreWidth((previous) => (Math.abs(previous - more) < 0.5 ? previous : more));
    };

    read();

    const observer = new ResizeObserver(read);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const read = () => setAvailable(strip.clientWidth);
    read();

    const observer = new ResizeObserver(read);
    observer.observe(strip);
    return () => observer.disconnect();
  }, []);

  const activeIndex = visible.findIndex((tab) => tab.key === activeKey);

  /*
    THE WIDTHS MUST DESCRIBE THE TABS BEING DRAWN. They are measured a frame AFTER the tab set
    changes, so a strip whose tabs come and go (the evidence case's For Evaluation, shown only
    while something waits) renders once with widths for the OLD set — and an index from the old,
    longer set named a tab that no longer existed, crashing the strip. Until the measurement
    catches up, every tab draws, exactly as on the first frame before anything was measured.
  */
  const measured = natural.length === visible.length;
  const keptIndexes = measured ? fitTabs(natural, moreWidth, available, activeIndex) : visible.map((_, index) => index);
  const kept = new Set(keptIndexes);
  const overflow = visible.filter((_, index) => !kept.has(index));
  const overflowCount = overflow.length;

  /*
    A window widened while the dropdown is open leaves it holding nothing, and its trigger
    unmounts with the last overflowing tab — so the panel would float over the strip anchored
    to a control that is no longer there.

    ADJUSTED DURING RENDER rather than in an effect: React re-renders immediately without
    committing this pass, so the stale panel is never painted. An effect would close it one
    paint later, which is the frame being prevented.
  */
  if (open && overflowCount === 0) {
    setOpen(false);
  }

  // Every tab gated away: the whole strip goes, rather than leaving an empty rail.
  if (visible.length === 0) {
    return null;
  }

  /** Past the cap, so the strip is showing part of a name — see `TAB_LABEL`. */
  const isTruncated = (index: number) => (natural[index] ?? 0) >= MAX_TAB_WIDTH;

  function renderTab(tab: FolderTab<K>, measuring: boolean) {
    const isActive = tab.key === activeKey;

    return (
      <button
        key={tab.key}
        type="button"
        id={!measuring && id ? `${id}-tab-${tab.key}` : undefined}
        role={measuring ? undefined : 'tab'}
        aria-selected={measuring ? undefined : isActive}
        aria-hidden={measuring || undefined}
        tabIndex={measuring ? -1 : undefined}
        disabled={tab.disabled}
        onClick={measuring ? undefined : () => onChange(tab.key)}
        /*
          Borders are inline so they survive Tailwind v4's class-detection timing.

          Every tab is outlined — an unselected tab is still a folder tab, and a row of bare
          labels beside one outlined shape reads as a single button rather than as a set to
          choose from. The BOTTOM edge is what separates them: the active tab closes its bottom
          in the panel's own ground, breaking the panel's top line so the two merge, while an
          inactive tab keeps that line running underneath it and stays visibly behind the panel.

          The HEIGHT and the CAP are inline for a different reason — `fitTabs` does arithmetic
          against both, and a number in a class string is a number the arithmetic cannot read.
        */
        style={{
          height: TAB_HEIGHT,
          maxWidth: measuring ? undefined : MAX_TAB_WIDTH,
          borderTopColor: isActive ? PANEL_BORDER : TAB_BORDER,
          borderLeftColor: isActive ? PANEL_BORDER : TAB_BORDER,
          borderRightColor: isActive ? PANEL_BORDER : TAB_BORDER,
          /*
            The bottom edge belongs to the PANEL, not to the tab — which is why it is the
            panel's tone on an inactive tab even though the other three sides are lighter. The
            active tab breaks that line in the panel's own ground to merge with it; an inactive
            tab lets it run underneath, unbroken and one colour all the way across.
          */
          borderBottomColor: isActive ? PANEL : PANEL_BORDER,
        }}
        /*
          `text-xs` (13px on the +1px scale) — a tab is a label, not body copy, and at
          `text-sm` a strip of them competed with the panel content underneath.
        */
        className={[TAB_SHAPE, surfaceOf(tab, isActive), labelOf(tab, isActive)].join(' ')}
      >
        <span className={TAB_LABEL}>{tab.label}</span>
      </button>
    );
  }

  return (
    <div className={`relative flex items-end gap-3 ${paddingX}`}>
      <div ref={stripRef} className="flex min-w-0 flex-1 items-end gap-1">
        <div role="tablist" aria-label={ariaLabel} className="flex items-end gap-1">
          {/*
            A CLIPPED NAME IS STILL THE NAME, so a tab the cap has shortened carries the whole
            of it in a `<Tooltip>` — the rule `<TextCell>` already follows for a table cell it
            had to ellipsize. Wrapped only when it fires: a tooltip repeating a label that is
            fully on screen is noise, which is why the cap is measured rather than assumed.

            The wrapper sits INSIDE `role="tablist"` and the tabs stay its descendants. It is
            a bare `<span>` with no role, so the accessibility tree prunes it and the tablist
            still owns the tabs; putting it inside the button instead would stop the label
            shrinking at all, because a flex item's `min-width` defaults to its content.
          */}
          {keptIndexes.map((index) =>
            isTruncated(index) ? (
              <Tooltip key={visible[index].key} label={visible[index].label}>
                {renderTab(visible[index], false)}
              </Tooltip>
            ) : (
              renderTab(visible[index], false)
            ),
          )}
        </div>

        {overflowCount > 0 ? (
          <div ref={triggerRef} className="flex items-end">
            <button
              type="button"
              id={id ? `${id}-tab-overflow` : undefined}
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
              style={{
                height: TAB_HEIGHT,
                borderTopColor: TAB_BORDER,
                borderLeftColor: TAB_BORDER,
                borderRightColor: TAB_BORDER,
                borderBottomColor: PANEL_BORDER,
              }}
              className={`${TAB_SHAPE} gap-1 bg-slate-50 text-slate-600 hover:bg-white`}
            >
              {overflowCount} more
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      {rightSlot ? <div className="flex items-center gap-2 pb-1.5">{rightSlot}</div> : null}

      {/*
        THE MEASURING ROW — every tab at full width, plus the dropdown trigger.

        `absolute` so it contributes nothing to layout, `invisible` so it paints nothing, and
        `aria-hidden` with `tabIndex={-1}` so it is in neither the accessibility tree nor the
        tab run. It is the only way to know what a tab in the dropdown WOULD measure, which is
        what decides whether it belongs there at all.
      */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute top-0 left-0 flex items-end gap-1"
      >
        {visible.map((tab) => renderTab(tab, true))}
        <button
          type="button"
          tabIndex={-1}
          style={{ height: TAB_HEIGHT }}
          className={`${TAB_SHAPE} gap-1 bg-slate-50 text-slate-600`}
        >
          {visible.length} more
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              aria-label={ariaLabel ? `${ariaLabel} — more` : 'More tabs'}
              style={{ position: 'fixed', top: rect.top, left: rect.left }}
              className={`${LAYER.PANEL} modern-scrollbar max-h-80 w-max max-w-80 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-raised`}
            >
              {overflow.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="menuitem"
                  disabled={tab.disabled}
                  onClick={() => {
                    onChange(tab.key);
                    setOpen(false);
                  }}
                  /*
                    THE SAME 36px A TAB IS, and for the same reason one surface in: a row
                    carrying a `<CountBadge>` is 33px tall and one without is 29px, so a
                    dropdown of four reads as ragged as the strip it was built to fix.
                  */
                  style={{ height: TAB_HEIGHT }}
                  className={[
                    'flex w-full items-center px-3 text-left text-xs transition',
                    tab.disabled
                      ? 'cursor-not-allowed text-slate-300'
                      : 'text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  <span className="min-w-0 flex-1 truncate [&>*]:ml-4 [&>*]:align-middle">
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
